import type { Subscription } from '@rails/actioncable'
import { sharedConsumer } from '../../lib/cable'
import type { DanmakuLine } from './types'

// Tells VirtualOffice::PresenceChannel this tab is open, and whether anyone is
// using it, so /virtual_office can show who is here. Started by the header
// bell, which only renders for signed-in people, so visitors never report
// anything.
//
// Only subscriptions of people the office is open to (VirtualOffice::AccessGrant)
// receive anything back: "something changed" pings (onOfficeChanged) and 弹幕
// lines (onDanmaku).
const HEARTBEAT_MS = 30_000
const ACTIVE_WINDOW_MS = 5 * 60_000
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const

let subscription: Subscription | null = null
let lastActivity = Date.now()
// This tab is playing music in the office (headphones on).
let listening = false
const changeListeners = new Set<() => void>()
const danmakuListeners = new Set<(line: DanmakuLine) => void>()

function isActive() {
  return document.visibilityState === 'visible' && Date.now() - lastActivity < ACTIVE_WINDOW_MS
}

function receive(data: { changed?: boolean; danmaku?: DanmakuLine }) {
  if (data.danmaku) {
    const line = data.danmaku
    danmakuListeners.forEach((listener) => listener(line))
  } else {
    changeListeners.forEach((listener) => listener())
  }
}

export function ensurePresenceStream() {
  if (subscription || typeof window === 'undefined') return

  const mark = () => {
    lastActivity = Date.now()
  }
  ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, mark, { passive: true }))

  subscription = sharedConsumer().subscriptions.create(
    { channel: 'VirtualOffice::PresenceChannel', active: isActive(), listening },
    {
      received: receive,
      rejected: () => console.error('Connection to VirtualOffice::PresenceChannel was rejected'),
    },
  )

  window.setInterval(() => subscription?.perform('heartbeat', { active: isActive(), listening }), HEARTBEAT_MS)
  // Coming back to the tab should not wait up to 30 seconds to read as "here" again.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    mark()
    subscription?.perform('heartbeat', { active: true, listening })
  })
}

// Music started or stopped in the office: tell everyone now, not at the next heartbeat.
export function setListening(next: boolean) {
  if (listening === next) return
  listening = next
  subscription?.perform('heartbeat', { active: isActive(), listening })
}

export function onOfficeChanged(listener: () => void) {
  changeListeners.add(listener)
  return () => {
    changeListeners.delete(listener)
  }
}

export function onDanmaku(listener: (line: DanmakuLine) => void) {
  danmakuListeners.add(listener)
  return () => {
    danmakuListeners.delete(listener)
  }
}

// The server trims, rate-limits and only accepts lines from people the office
// is open to (VirtualOffice::Danmaku); the sender sees their own line when it
// comes back.
export function sendDanmaku(text: string) {
  subscription?.perform('say', { text })
}
